import clsx from "clsx";
import {useCallback, useMemo} from "react";
import {Badge, ProgressBar} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {
    Bar,
    BarChart,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    getCategoricalColor,
    getCategoricalNeutral,
} from "../../../utils/ColorUtils";
import LoadingComponent from "../../loading/LoadingComponent";
import Checkbox from "../checkbox/Checkbox";
import InfoBox from "../info-box/InfoBox";
import LabeledSelect from "../select/labeled-select/LabeledSelect";
import {
    buildChartData,
    formatBarLabel,
    formatColumnValue,
    formatColumnWarning,
    formatGroupLabel,
    PARENT_CHIPS_LIMIT,
    PREVIEW_GROUPS_LIMIT,
} from "./asyncTableDistribution";

// wartość "Wszystkie" w liście wyboru grupy nadrzędnej (klucz grupy może być dowolny, także null)
const ALL_PARENTS = "__all__";

// wysokość wykresu rozkładu (px) - recharts liczy w pikselach
const CHART_HEIGHT = 112;

const CHART_MARGIN = {top: 4, right: 0, bottom: 0, left: 0};

/**
 * Rozkład wartości kolumny - słupki z `Profile.Bars` w kolejności osi (recharts). Słupek to stos
 * segmentów: grupy wyrażenia (`BarGroups`), grupy nadrzędne (`split`) albo jeden kolor. Front niczego
 * nie liczy. Słupek zbiorczy na brzegu (`edge`) przerywaną obwódką, niepełny okres (`partial`) jaśniejszy.
 * @param {Object} props
 * @param {Object} props.profile `Profile` z API
 * @param {Array} [props.barGroups] `Preview.BarGroups` - tylko gdy pasuje do słupków
 * @param {"groups"|"parents"|"none"} props.colorBy
 * @param {number} props.bucketsCount liczba przedziałów wyrażenia
 * @param {Object} props.column nagłówek kolumny poziomu
 * @param {function(string): string} props.segmentLabel nazwa segmentu (grupy) po kluczu - w podpowiedzi
 */
export function DistributionChart({
    profile,
    barGroups,
    colorBy,
    bucketsCount,
    column,
    segmentLabel,
}) {
    const {t, i18n} = useTranslation();
    const {Bars: bars = [], Column: stats, ColumnWarnings = []} = profile;

    const {rows, series} = useMemo(
        () =>
            buildChartData(
                bars,
                bars.map((bar) =>
                    formatBarLabel(bar.label, column, t, i18n.language)
                ),
                {
                    colorBy,
                    barGroups,
                    bucketsCount,
                    parentGroups: profile.ParentGroups,
                }
            ),
        [
            bars,
            column,
            t,
            i18n.language,
            colorBy,
            barGroups,
            bucketsCount,
            profile.ParentGroups,
        ]
    );

    // nagłówek podpowiedzi - etykieta słupka, przy niepełnym okresie dopisek
    const tooltipLabel = useCallback(
        (label, payload) =>
            payload?.[0]?.payload?.partial
                ? `${label} · ${t("groupDistributionPartial")}`
                : label,
        [t]
    );

    const tooltipValue = useCallback(
        (value) => t("groupRowsCount", {count: value}),
        [t]
    );

    if (bars.length === 0) {
        const warnings = ColumnWarnings.map((code) =>
            formatColumnWarning(code, t)
        ).filter(Boolean);
        // ostrzeżenia kolumny pokazuje lista ostrzeżeń pod grupami
        if (warnings.length > 0) return null;
        return (
            <InfoBox className="async-table-group-rule-info">
                <i
                    className="fa-solid fa-chart-column me-2"
                    aria-hidden="true"
                />
                <span>{t("groupDistributionEmpty")}</span>
            </InfoBox>
        );
    }

    return (
        <div className="async-table-group-rule-chart">
            <div className="async-table-group-rule-caption">
                <span>{t("groupDistributionTitle")}</span>
                {stats?.min !== null && stats?.min !== undefined && (
                    <span>
                        {t("groupDistributionRange", {
                            min: formatColumnValue(
                                stats.min,
                                i18n.language,
                                column
                            ),
                            max: formatColumnValue(
                                stats.max,
                                i18n.language,
                                column
                            ),
                        })}
                    </span>
                )}
            </div>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={rows} margin={CHART_MARGIN} barCategoryGap={1}>
                    <XAxis
                        dataKey="label"
                        interval="preserveStartEnd"
                        minTickGap={8}
                        tickLine={false}
                    />
                    <YAxis hide />
                    <Tooltip
                        isAnimationActive={false}
                        labelFormatter={tooltipLabel}
                        formatter={tooltipValue}
                    />
                    {series.map((item) => (
                        <Bar
                            key={item.key}
                            dataKey={item.dataKey}
                            name={segmentLabel(item.key)}
                            stackId="distribution"
                            fill={item.color}
                            isAnimationActive={false}>
                            {rows.map((row, index) => (
                                <Cell
                                    key={index}
                                    className={clsx({
                                        edge: row.edge,
                                        partial: row.partial,
                                    })}
                                    stroke={row.edge ? item.color : undefined}
                                    strokeDasharray={
                                        row.edge ? "3 2" : undefined
                                    }
                                />
                            ))}
                        </Bar>
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

/**
 * Legenda grup nadrzędnych (kolory podziału `split`).
 * @param {Object} props
 * @param {Array} props.parentGroups `Profile.ParentGroups`
 * @param {function(*): string} props.parentLabel etykieta grupy nadrzędnej po kluczu
 */
export function ParentLegend({parentGroups, parentLabel}) {
    return (
        <ul className="async-table-group-rule-legend">
            {parentGroups.map((group, index) => (
                <li key={String(group.key)}>
                    <span
                        className="async-table-group-rule-swatch"
                        style={{
                            background:
                                getCategoricalColor(index) ??
                                getCategoricalNeutral(),
                        }}
                        aria-hidden="true"
                    />
                    {parentLabel(group.key)}
                </li>
            ))}
        </ul>
    );
}

/**
 * Przełącznik nad wykresem: "Wszystkie" i grupy poziomu wyżej - chipy, przy większej liczbie lista wyboru.
 * Wybór zawęża rozkład i podgląd do jednej grupy nadrzędnej (`ParentKey`).
 * @param {Object} props
 * @param {Array} props.parentGroups `Profile.ParentGroups`
 * @param {*} props.value klucz wybranej grupy, null - wszystkie
 * @param {function(*)} props.onChange
 * @param {Object} props.parentColumn nagłówek kolumny poziomu wyżej
 * @param {function(*): string} props.groupLabel etykieta grupy nadrzędnej po kluczu
 * @param {boolean} props.showParents kolory grup nadrzędnych na wykresie
 * @param {function(boolean)} [props.onShowParentsChange] brak - przełącznika kolorów nie ma
 */
export function ParentSwitch({
    parentGroups,
    value,
    onChange,
    parentColumn,
    groupLabel,
    showParents,
    onShowParentsChange,
}) {
    const {t} = useTranslation();

    const options = useMemo(
        () => [
            {value: ALL_PARENTS, name: t("all")},
            ...parentGroups.map((group) => ({
                value: String(group.key),
                name: groupLabel(group.key),
            })),
        ],
        [parentGroups, groupLabel, t]
    );

    // "Grupa: <kolumna poziomu wyżej>" - nazwa kolumny doklejona do tekstu
    const parentLabel = parentColumn?.name
        ? `${t("group")}: ${parentColumn.name}`
        : t("group");

    const selected = value === null ? ALL_PARENTS : String(value);

    // klucze porównujemy jako tekst, do API idzie klucz z ParentGroups bez konwersji
    const onSelect = useCallback(
        (option) => {
            if (option === ALL_PARENTS) {
                onChange(null);
                return;
            }
            const group = parentGroups.find(
                (item) => String(item.key) === option
            );
            onChange(group ? group.key : null);
        },
        [parentGroups, onChange]
    );

    return (
        <div className="async-table-group-rule-parents">
            {parentGroups.length <= PARENT_CHIPS_LIMIT ? (
                <div
                    className="async-table-group-rule-chips"
                    role="radiogroup"
                    aria-label={parentLabel}>
                    {options.map((option) => (
                        <Badge
                            key={option.value}
                            as="button"
                            type="button"
                            pill
                            bg={
                                option.value === selected
                                    ? "primary"
                                    : "secondary"
                            }
                            role="radio"
                            aria-checked={option.value === selected}
                            className="async-table-group-rule-chip"
                            onClick={() => onSelect(option.value)}>
                            {option.name}
                        </Badge>
                    ))}
                </div>
            ) : (
                <LabeledSelect
                    id="async-table-group-rule-parent"
                    label={parentLabel}
                    value={selected}
                    onChange={onSelect}
                    options={options}
                    clearButton={false}
                />
            )}
            {onShowParentsChange && (
                <Checkbox
                    id="async-table-group-rule-show-parents"
                    label={t("groupDistributionShowParents")}
                    checked={showParents}
                    onChange={onShowParentsChange}
                />
            )}
        </div>
    );
}

/**
 * Propozycje podziału z API - kliknięcie dopiero zmienia konfigurację.
 * @param {Object} props
 * @param {Array<{suggestion: Object, label: string}>} props.items
 * @param {function(Object)} props.onApply
 */
export function Suggestions({items, onApply}) {
    const {t} = useTranslation();
    if (items.length === 0) return null;
    return (
        <div className="async-table-group-rule-suggestions">
            <span className="async-table-group-rule-caption">
                {t("suggestions")}
            </span>
            <div className="async-table-group-rule-chips">
                {items.map(({suggestion, label}, index) => {
                    const title =
                        suggestion.kind === "buckets"
                            ? suggestion.groups
                                  ?.map((group) =>
                                      t("groupRowsCount", {
                                          count: group.count,
                                      })
                                  )
                                  .join(" · ")
                            : t("groupRuleGroupsPreview", {
                                  count: suggestion.groupsCount,
                              });
                    return (
                        <Badge
                            key={index}
                            as="button"
                            type="button"
                            pill
                            bg="info"
                            className="async-table-group-rule-chip"
                            title={title}
                            onClick={() => onApply(suggestion)}>
                            <i
                                className="fa-solid fa-wand-magic-sparkles me-1"
                                aria-hidden="true"
                            />
                            {label}
                        </Badge>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Ostrzeżenia podglądu i kolumny pod listą grup.
 * @param {{messages: string[]}} props
 */
export function DistributionWarnings({messages}) {
    if (messages.length === 0) return null;
    return (
        <div className="async-table-group-rule-warnings" role="status">
            {messages.map((message, index) => (
                <InfoBox
                    key={index}
                    boxColor="warning"
                    className="async-table-group-rule-info">
                    <i
                        className="fa-solid fa-triangle-exclamation me-2"
                        aria-hidden="true"
                    />
                    <span>{message}</span>
                </InfoBox>
            ))}
        </div>
    );
}

/**
 * Miejsce rozkładu (wykres, lista grup) z ładowaniem nad treścią. Przy odświeżaniu stara treść
 * zostaje przyciemniona, bez treści - samo ładowanie albo błąd.
 * @param {Object} props
 * @param {boolean} props.loading zapytanie w drodze (albo jeszcze nie wysłane)
 * @param {boolean} [props.error] zapytanie się nie udało
 * @param {*} [props.children] treść z ostatniej odpowiedzi
 */
export function DistributionArea({loading, error, children}) {
    const {t} = useTranslation();
    return (
        <div
            className={clsx("async-table-group-rule-area", {
                // miejsce na spinner tylko w trakcie ładowania - bez wykresu nic nie zostaje
                pending: loading && !children,
                refreshing: loading && children,
            })}>
            {children}
            {error && !loading && !children && (
                <InfoBox
                    boxColor="error"
                    className="async-table-group-rule-info">
                    <i
                        className="fa-solid fa-circle-exclamation me-2"
                        aria-hidden="true"
                    />
                    <span>{t("errorOccuredWhenFetchingData")}</span>
                </InfoBox>
            )}
            <LoadingComponent isLoading={loading} bgInherit />
        </div>
    );
}

/**
 * Podgląd "Każdej wartości": pierwsze grupy z odpowiedzi API (już w kolejności grup) z pasem
 * liczby wierszy, reszta liczbą.
 * @param {Object} props
 * @param {Object} props.preview `Preview` z API
 * @param {Object} props.column nagłówek kolumny poziomu
 * @param {Object} [props.childColumn] nagłówek kolumny poziomu niżej (podgrupy)
 */
export function EachValuePreview({preview, column, childColumn}) {
    const {t} = useTranslation();
    const groups = (preview.Groups ?? []).slice(0, PREVIEW_GROUPS_LIMIT);
    const maxCount = Math.max(1, ...groups.map((group) => group.count));
    const allCount = preview.AllGroupsCount ?? preview.Groups?.length ?? 0;
    const more = allCount - groups.length;
    return (
        <div>
            <div className="async-table-group-rule-caption">
                {t("groupRuleGroupsPreview", {count: allCount})}
            </div>
            <ul className="async-table-group-rule-values">
                {groups.map((group) => {
                    const label = formatGroupLabel(group.label, column, t);
                    return (
                        <li key={String(group.key)}>
                            <b>{label}</b>
                            <ProgressBar
                                className="async-table-group-rule-value-bar"
                                now={group.count}
                                max={maxCount}
                                aria-label={label}
                            />
                            <span className="async-table-group-rule-group-count">
                                {t("groupRowsCount", {count: group.count})}
                                {group.subgroups !== undefined && (
                                    <span className="d-block">
                                        {t("groupSubgroupsCount", {
                                            count: group.subgroups,
                                            name: childColumn?.name ?? "",
                                        })}
                                    </span>
                                )}
                            </span>
                        </li>
                    );
                })}
            </ul>
            {more > 0 && (
                <div className="async-table-group-rule-caption">
                    {t("XmoreParams", {number: more})}
                </div>
            )}
        </div>
    );
}
